import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';

const BeeSelector = ({ onBeeSelected, selectedCompetitor }) => {
  const { user } = useAuth();
  const [bees, setBees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedBee, setSelectedBee] = useState(null);
  const isDevelopment = process.env.REACT_APP_ENV === 'development';
  const useLocalStack = process.env.REACT_APP_USE_LOCALSTACK === 'true';
  

  useEffect(() => {
    fetchBees();
  }, []);

  const fetchBees = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (isDevelopment && !useLocalStack) {
        // Mock data for local demo (without LocalStack)
        const mockBees = [
          {
            bee_id: 'bee_12345678',
            name: '2024 Regional Spelling Bee',
            bee_date: '2024-03-15T09:00:00.000Z'
          },
          {
            bee_id: 'bee_87654321',
            name: '2024 State Championship', 
            bee_date: '2024-04-20T10:00:00.000Z'
          },
          {
            bee_id: 'bee_11223344',
            name: 'Elementary Practice Bee',
            bee_date: '2024-02-10T14:00:00.000Z'
          },
          {
            bee_id: 'bee_99887766',
            name: 'Advanced Vocabulary Challenge',
            bee_date: '2024-05-15T13:00:00.000Z'
          }
        ];
        
        // Simulate API delay
        setTimeout(() => {
          setBees(mockBees);
          setLoading(false);
        }, 800);
      } else {
        // Real API call for production or LocalStack
        const response = await apiService.getAllBees();
        setBees(response.bees || []);
        setLoading(false);
      }
      
    } catch (err) {
      setError('Failed to load spelling bees. Please try again.');
      setLoading(false);
    }
  };

  const handleBeeSelection = async (bee) => {
    try {
      setError(null);
      
      if (isDevelopment && !useLocalStack) {
        // Simulate API call delay (mock mode only)
        setTimeout(() => {
          setSelectedBee(bee);
          onBeeSelected(bee);
        }, 500);
      } else {
        // Real API call for production - use competitor if available
        try {
          if (selectedCompetitor) {
            await apiService.addCompetitorToBee(selectedCompetitor.competitor_id, bee.bee_id);
          } else {
            await apiService.addUserToBee(user.user_id, bee.bee_id);
          }
        } catch (addError) {
          // If the error is that competitor/user is already in the bee, that's fine - continue
          if (addError.message.includes('already in this bee') ||
              addError.message.includes('already exists') ||
              addError.message.includes('Duplicate') ||
              addError.message.includes('HTTP error! status: 409')) {
            console.log('Competitor/user already in bee, proceeding to practice');
            // User/competitor already in bee, proceeding to practice
          } else {
            // Re-throw other errors
            throw addError;
          }
        }
        setSelectedBee(bee);
        onBeeSelected(bee);
      }
      
    } catch (err) {
      setError(`Failed to join ${bee.name}. Please try again.`);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="card">
        <div className="loading">Loading spelling bees...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card">
        <div className="error">{error}</div>
        <button onClick={fetchBees} className="button">
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="card">
      <h2 className="title">Choose a Spelling Bee</h2>
      <p className="subtitle">
        {selectedCompetitor 
          ? `Select a spelling bee for ${selectedCompetitor.name} to practice`
          : 'Select a spelling bee to start practicing'
        }
      </p>
      
      <div className="bee-selector">
        {bees.length === 0 ? (
          <div className="loading">No spelling bees available.</div>
        ) : (
          <div className="bee-list">
            {bees.map((bee) => (
              <div
                key={bee.bee_id}
                className={`bee-item ${selectedBee?.bee_id === bee.bee_id ? 'selected' : ''}`}
                onClick={() => handleBeeSelection(bee)}
              >
                <div className="bee-name">{bee.name}</div>
                <div className="bee-date">{formatDate(bee.bee_date)}</div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {selectedBee && (
        <div style={{ marginTop: '2rem', padding: '1rem', background: '#f7fafc', borderRadius: '8px' }}>
          <p><strong>Selected:</strong> {selectedBee.name}</p>
          <p>Click "Start Practice" to begin!</p>
        </div>
      )}
    </div>
  );
};

export default BeeSelector;